import { useState, useMemo } from 'react';
import shortid from 'shortid';
import { Box, Button, Modal, Select, Text } from '@rsos/base-components';
import {
  ModalBody,
  ModalFooter,
  ModalHeader,
} from '@rsos/base-components/Modal/ModalComponents';
import {
  useCancelTransportMutation,
  type EtlTransportResponse,
} from '@rsos/reduxDataStore/src/features/resourceRequests';
import {
  trackCancelAirAmbulanceRequest,
  trackCancelAirAmbulanceRequestApiResponse,
} from '../../../gainsightTrackings/airAmbulanceTrackings';
import { CANCEL_REQUEST_REASONS } from '../AirAmbulanceForm/constants';
import { getErrorMessage, getErrorStatusCode } from '../AirAmbulanceForm/utils';
import type { SelectOption } from '../types';

interface AirAmbulanceEtlCancelModalProps {
  transport: EtlTransportResponse;
  showModal: boolean;
  onClose: () => void;
}

function AirAmbulanceEtlCancelModal({
  transport,
  showModal,
  onClose,
}: AirAmbulanceEtlCancelModalProps): JSX.Element {
  const [cancelReason, setCancelReason] = useState<SelectOption | null>(null);
  const [cancelTransport] = useCancelTransportMutation();
  const UUID = useMemo(() => shortid.generate(), []);

  const onSelectChange = (selectedOption: SelectOption | null): void => {
    setCancelReason(selectedOption);
  };

  const onConfirmCancel = async (): Promise<void> => {
    if (!cancelReason) return;

    trackCancelAirAmbulanceRequest({
      requestId: transport.gmr_transport_id,
      reason: cancelReason.value,
    });

    try {
      await cancelTransport({
        transportId: transport.id,
        reason: cancelReason.value,
      }).unwrap();

      trackCancelAirAmbulanceRequestApiResponse({
        requestId: transport.gmr_transport_id,
        status: 200,
        error: false,
        success: true,
        UUID,
      });
    } catch (error) {
      trackCancelAirAmbulanceRequestApiResponse({
        requestId: transport.gmr_transport_id,
        status: getErrorStatusCode(error),
        error: getErrorMessage(error),
        success: false,
        UUID,
      });

      // eslint-disable-next-line no-console -- track errors in sentry
      console.error('Cancel transport error:', error);
    }

    setCancelReason(null);
    onClose();
  };

  return (
    <Modal
      id="air-ambulance-etl-cancel-request-modal"
      isOpened={showModal}
      onClose={onClose}
      padding="45px 45px 35px"
      width={400}
    >
      <ModalHeader onClose={onClose}>
        <Text color="primaryText" size="large">
          Cancel Air Ambulance Request?
        </Text>
      </ModalHeader>
      <ModalBody>
        <Text color="secondaryText" size="small">
          Are you sure you want to cancel the request for air ambulance?
        </Text>
        <Box mt={2}>
          <Select
            options={CANCEL_REQUEST_REASONS}
            value={cancelReason}
            onChange={onSelectChange}
            placeholder="Select a cancellation reason (required)"
          />
        </Box>
      </ModalBody>
      <ModalFooter>
        <Box horizontal justify="center" width="100%">
          <Button grow btnType="tertiary" onClick={onClose}>
            Keep Request
          </Button>
          <Button
            grow
            btnType="warning_tertiary"
            onClick={onConfirmCancel}
            disabled={!cancelReason}
          >
            Cancel Request
          </Button>
        </Box>
      </ModalFooter>
    </Modal>
  );
}

export default AirAmbulanceEtlCancelModal;
